import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { useTheme } from 'styled-components'
import { formatPercentage } from '@chilecompra/react-kit'
import { Button, DataTable, Select, useSelect } from '@chilecompra/react-kit/components'
import { CURRENCY_VALUES } from '../../config/settings/constants'
import exportToImage, { exportToCsv } from '../../modules/utils/exports'
import { formatAmount } from '../../modules/utils/formatters'
import generateYearsBetween from '../../modules/utils/years'
import BubblesChart from '../../components/Charts/BubblesChart'
import PieChart from '../../components/Charts/PieChart'
import RegionalProviders from '../../components/Infographics/RegionalProviders'
import DataLayout, {
  DataLayoutChild,
  DataLayoutViewButton,
  DataLayoutViews,
  useDataLayout
} from '../../components/Layouts/DataLayout'
import routes from '../../config/settings/routes'
import { onGetRegionalProvidersThunk, onGetRegionalProviderThunk } from './RegionalProvidersSection.actions'
import {
  BubblesDetail,
  BubblesItem,
  BubblesLabel,
  BubblesList,
  BubblesTitle,
  BubblesValue,
  Description,
  DownloadDetails,
  DownloadDetailsCircle,
  DownloadDetailsIcon,
  DownloadDetailsText,
  GridContainer,
  GridItem,
  Info,
  Link,
  LocationIcon,
  Podium,
  Ranking,
  Return,
  ReturnIcon,
  ReturnText,
  Root,
  Strong,
  Title
} from './RegionalProvidersSection.styles'

const DataLayoutCustomViews = {
  CHART2: 3
}

const SortDirectionDictionary = {
  ascending: 'asc',
  descending: 'desc'
}

const SortOrderDictionary = {
  amount: 'amount',
  dni: 'rut',
  name: 'name',
  orders: 'oc',
  totalAmount: 'status'
}

/**
 * The RegionalProvidersSection's container.
 */
const RegionalProvidersSection = () => {
  const dispatch = useDispatch()
  const theme = useTheme()
  const navigate = useNavigate()
  const { endYear, latestUpdate, loading, startYear } = useSelector(state => state.topNavbar)
  const { id: regionId, name: regionName } = useSelector(state => state.regionalSection)
  const { provider, providers } = useSelector(state => state.regionalProvidersSection)
  const [sort, setSort] = useState({ direction: 'ascending', order: 'amount' })
  const [selectedProvider, setSelectedProvider] = useState(null)
  const [selectedProviderView, setSelectedProviderView] = useState(DataLayoutViews.CHART)
  const { value: yearValue, onChange: handleChangeYear } = useSelect({
    initialValue: endYear,
    overwriteCallback: () => endYear
  })
  const { value: detailsYearValue, onChange: handleChangeDetailsYear } = useSelect({
    initialValue: endYear,
    changeCallback: updatedValue =>
      dispatch(onGetRegionalProviderThunk({ regionId, dni: selectedProvider.dni, year: updatedValue })),
    overwriteCallback: () => endYear
  })
  const {
    disableDownloadFile: disableProviderDownloadFile,
    ref: providerDataLayoutRef,
    onCaptureImage: handleProviderCaptureImage,
    onChangeView: onChangeProviderView,
    onDownloadFile: handleProviderDownloadFile
  } = useDataLayout({
    customRules: {
      captureImageRule: view => view !== DataLayoutViews.CHART || view !== DataLayoutCustomViews.CHART2
    },
    dependencies: [detailsYearValue, provider.bubbleCsv, provider.pieCsv, selectedProviderView],
    captureImageCallback: () =>
      exportToImage(
        selectedProviderView === DataLayoutViews.CHART
          ? 'regional-provider-bubbles-chart'
          : 'regional-provider-pie-chart',
        selectedProviderView === DataLayoutViews.CHART
          ? `Info_Region_Proveedor_Burbuja_${detailsYearValue}`
          : `Info_Region_Proveedor_Pie_${detailsYearValue}`
      ),
    downloadFileCallback: () =>
      exportToCsv(
        selectedProviderView === DataLayoutViews.CHART ? provider.bubbleCsv : provider.pieCsv,
        selectedProviderView === DataLayoutViews.CHART
          ? `Info_Region_Proveedor_Burbuja_${detailsYearValue}`
          : `Info_Region_Proveedor_Pie_${detailsYearValue}`
      )
  })
  const yearsOptions = useMemo(() => generateYearsBetween(endYear, startYear), [endYear, startYear])
  const handleChangePage = useCallback(
    (_, updatedPage) =>
      dispatch(
        onGetRegionalProvidersThunk({
          regionId,
          direction: SortDirectionDictionary[sort.direction],
          order: SortOrderDictionary[sort.order],
          page: updatedPage,
          year: yearValue
        })
      ),
    [providers.page, sort.direction, sort.order, regionId, yearValue]
  )
  const handleSortColumn = useCallback(
    (updatedOrder, updatedDirection) =>
      dispatch(
        onGetRegionalProvidersThunk({
          regionId,
          direction: SortDirectionDictionary[updatedDirection],
          order: SortOrderDictionary[updatedOrder],
          page: providers.page,
          year: yearValue,
          callback: () => setSort({ direction: updatedDirection, order: updatedOrder })
        })
      ),
    [providers.page, sort.direction, sort.order, regionId, yearValue]
  )
  const handleResetProvider = useCallback(() => {
    setSelectedProvider(null)
    setSelectedProviderView(DataLayoutViews.CHART)
  }, [])
  const handleTooltipFormatter = useCallback(function () {
    return `
      <strong>${
        CURRENCY_VALUES.CLP
      }${formatAmount(this.y)} (${CURRENCY_VALUES.USD} ${formatAmount(this.point?.amountUSD)})</strong> transados en <strong>${this.x}</strong>
      <br />
      a través de ${this.point.orders} <strong>órdenes de compra</strong>.
    `
  }, [])
  const handlePieChartTooltipFormatter = useCallback(
    function () {
      return `
        <strong>${selectedProvider.name} en ${this.key}</strong> en el <strong>año ${detailsYearValue}</strong>:
        <br />
        <br />
        <ul>
          <li> - <strong>$${formatAmount(this.point.y)} ($USD ${formatAmount(
            this.point.amountUSD
          )})</strong> transados.</li>
          <br />
          <li>- Este corresponde al <strong>${formatPercentage(
            this.point?.percentage
          )}</strong> de su monto total transado durante el año.</li>
          <br />
          <li>- <strong>${this.point.orders}</strong> órdenes de compra.</li>
        </ul>
      `
    },
    [detailsYearValue, selectedProvider]
  )
  const handleChangeProviderView = useCallback(updatedView => {
    setSelectedProviderView(updatedView)
    onChangeProviderView(updatedView)
  }, [])
  const handleClickProviderView = useCallback(
    () => providerDataLayoutRef.current.setView(DataLayoutCustomViews.CHART2),
    []
  )
  const handleClickProvider = args => () =>
    dispatch(
      onGetRegionalProviderThunk({
        regionId,
        dni: args.dni,
        year: yearValue,
        callback: () => setSelectedProvider(args)
      })
    )
  const handleClickRedirect = dni => () => navigate(`${routes.provider.replace(/:dni/, dni)}`)
  const renderCustomControls = useCallback(
    (chartControl, tableControl) => (
      <>
        {chartControl}
        <DataLayoutViewButton
          selected={selectedProviderView === DataLayoutCustomViews.CHART2}
          onClick={handleClickProviderView}
        >
          <LocationIcon />
        </DataLayoutViewButton>
        {tableControl}
      </>
    ),
    [selectedProviderView]
  )
  const renderProviderDetails = useCallback(
    () => (
      <GridItem md={3} xs={12}>
        <GridContainer padding="8px 0 0">
          <GridItem xs={12}>
            <BubblesTitle>{selectedProvider.name}</BubblesTitle>
          </GridItem>
        </GridContainer>
        <GridContainer padding="24px 0 0">
          <GridItem xs={12}>
            <BubblesLabel>RUT</BubblesLabel>
            <BubblesValue>{selectedProvider.dni}</BubblesValue>
          </GridItem>
        </GridContainer>
        <GridContainer padding="24px 0 0">
          <GridItem xs={12}>
            <BubblesLabel>Monto total transado en la región</BubblesLabel>
            <BubblesValue>
              ${formatAmount(provider.amount)} <BubblesDetail>(USD ${formatAmount(provider.amountUSD)})</BubblesDetail>
            </BubblesValue>
            <BubblesList>
              <BubblesItem>
                <Strong>{formatPercentage(provider.percentage)}</Strong>% de su <Strong>monto transado</Strong> durante
                el año.
              </BubblesItem>
              <BubblesItem>
                <Strong>
                  ${formatAmount(provider.totalAmount)} ($USD {formatAmount(provider.totalAmountUSD)})
                </Strong>{' '}
                transado a nivel nacional durante el año.
              </BubblesItem>
            </BubblesList>
          </GridItem>
        </GridContainer>
        <GridContainer padding="24px 0 0">
          <GridItem xs={12}>
            <BubblesLabel>Órdenes de compra asociadas</BubblesLabel>
            <BubblesValue>{provider.orders}</BubblesValue>
          </GridItem>
        </GridContainer>
        <GridContainer padding="24px 0 0">
          <GridItem xs={12}>
            <BubblesLabel>Licitaciones adjudicadas</BubblesLabel>
            <BubblesValue>{provider.biddings}</BubblesValue>
          </GridItem>
        </GridContainer>
        <GridContainer padding="24px 0 0">
          <GridItem xs={12}>
            <Button color="primary" onClick={handleClickRedirect(selectedProvider.dni)}>
              Ver informe proveedor
            </Button>
          </GridItem>
        </GridContainer>
        <GridContainer padding="24px 0 0">
          <GridItem xs={12}>
            <DownloadDetails onClick={handleProviderDownloadFile}>
              <DownloadDetailsCircle>
                <DownloadDetailsIcon />
              </DownloadDetailsCircle>
              <DownloadDetailsText>Descargar estos datos</DownloadDetailsText>
            </DownloadDetails>
          </GridItem>
        </GridContainer>
      </GridItem>
    ),
    [detailsYearValue, provider, selectedProvider, selectedProviderView]
  )
  const enhancedProviderPieData = useMemo(
    () => provider.pie.map(datum => ({ ...datum, color: theme.palette.brand[datum.colorName] })),
    [provider.pie, regionId]
  )

  useEffect(() => {
    if (!regionId || !yearValue) {
      return
    }

    dispatch(
      onGetRegionalProvidersThunk({
        regionId,
        direction: SortDirectionDictionary[sort.direction],
        order: SortOrderDictionary[sort.order],
        page: providers.page,
        year: yearValue
      })
    )
  }, [providers.page, regionId, sort.direction, sort.order, yearValue])

  return (
    <Root>
      <GridContainer padding="25px 0 23px">
        <GridItem xs={12}>
          <Title>Proveedores</Title>
        </GridItem>
      </GridContainer>
      <GridContainer padding="0 0 28px">
        <GridItem xs={12}>
          {selectedProvider === null && (
            <Description>
              Revisa el detalle de todos los proveedores que hayan transado con alguna <Strong>unidad de compra</Strong>{' '}
              perteneciente a la <Strong>{regionName}</Strong> durante el año seleccionado.
            </Description>
          )}
          {selectedProvider !== null && (
            <Description>
              Revisa el detalle de todos los proveedores que hayan transado con alguna <Strong>unidad de compra</Strong>{' '}
              perteneciente a la <Strong>{regionName}</Strong> durante el año seleccionado.
            </Description>
          )}
        </GridItem>
      </GridContainer>
      {selectedProvider === null && (
        <GridContainer>
          <GridItem xs={12}>
            <DataLayout
              disableCaptureImage
              disableDownloadFile
              disableHeader
              initialDisplayedView={DataLayoutViews.TABLE}
              noDataText="El organismo comprador no cuenta con órdenes de compra que requieran una ficha de contrato en el año seleccionado."
              updatedDate={latestUpdate}
            >
              <DataLayoutChild view={DataLayoutViews.TABLE}>
                <GridContainer padding="0 0 24px">
                  <GridItem xs={12}>
                    <DataTable
                      enableResults
                      customPagination={{
                        page: providers.page,
                        pages: providers.pages,
                        records: providers.records,
                        onChangePage: handleChangePage,
                        onSortColumn: handleSortColumn
                      }}
                      header={
                        <GridContainer alignItems="center" spacing={1}>
                          <GridItem md={2} xs={12}>
                            <Select
                              label="AÑO"
                              loading={loading}
                              options={yearsOptions}
                              value={yearValue}
                              onChange={handleChangeYear}
                            />
                          </GridItem>
                          <GridItem md={10} xs={12} />
                        </GridContainer>
                      }
                      headers={[
                        { label: 'Ranking', value: 'ranking' },
                        { label: 'Nombre proveedor', value: 'name' },
                        { label: 'RUT proveedor', value: 'dni' },
                        { label: 'N° de órdenes de compra', value: 'orders' },
                        { label: 'Monto transado región', value: 'amount' },
                        { label: 'Monto transado región (USD)', value: 'amountUSD' },
                        { label: 'Total monto nacional', value: 'totalAmount' },
                        { label: 'Total monto nacional (USD)', value: 'totalAmountUSD' }
                      ]}
                      // FIXME: there is no way to filter using the related service.
                      headersCellsProps={[
                        { width: '120px' },
                        { enableSort: true, width: '300px' },
                        { enableSort: true, width: '150px' },
                        { enableSort: true, width: '150px' },
                        { enableSort: true, initialDirection: 'ascending', initialSort: true, width: '180px' },
                        { width: '180px' },
                        { enableSort: true, width: '180px' },
                        { width: '210px' }
                      ]}
                      loading={loading}
                      records={providers.data}
                      recordsDecorators={[
                        ({ ranking }) =>
                          [1, 2, 3].indexOf(ranking) !== -1 ? (
                            <Podium>{ranking}</Podium>
                          ) : (
                            <Ranking>{ranking || ''}</Ranking>
                          ),
                        args => <Link onClick={handleClickProvider(args)}>{args.name}</Link>,
                        ({ dni }) => dni,
                        ({ orders }) => orders,
                        ({ amount }) => `$${formatAmount(amount)}`,
                        ({ amountUSD }) => `$${formatAmount(amountUSD)}`,
                        ({ totalAmount }) => `$${formatAmount(totalAmount)}`,
                        ({ totalAmountUSD }) => `$${formatAmount(totalAmountUSD)}`
                      ]}
                    />
                  </GridItem>
                </GridContainer>
                <GridContainer margin="6px 0 0">
                  <GridItem xs={12}>
                    <Info>
                      La información de montos transados dentro de esta sección corresponde al monto bruto (monto neto +
                      impuestos)
                    </Info>
                  </GridItem>
                </GridContainer>
              </DataLayoutChild>
            </DataLayout>
          </GridItem>
        </GridContainer>
      )}
      {selectedProvider !== null && (
        <GridContainer>
          <GridItem xs={12}>
            <DataLayout
              custom={{ views: [DataLayoutCustomViews.CHART2], controls: renderCustomControls }}
              disableCaptureImage={selectedProviderView === DataLayoutViews.TABLE}
              disableDownloadFile={disableProviderDownloadFile}
              header={
                <GridContainer alignItems="center" spacing={1}>
                  <GridItem md={2} xs={12}>
                    <Select
                      label="AÑO"
                      loading={loading}
                      options={yearsOptions}
                      value={detailsYearValue}
                      onChange={handleChangeDetailsYear}
                    />
                  </GridItem>
                  <GridItem md={10} xs={12} />
                </GridContainer>
              }
              headerProps={{ leftItem: 10, padding: '0', rightItem: 2 }}
              infographic={<RegionalProviders />}
              noDataText="El organismo comprador no cuenta con órdenes de compra que requieran una ficha de contrato en el año seleccionado."
              ref={providerDataLayoutRef}
              updatedDate={latestUpdate}
              onCaptureImage={handleProviderCaptureImage}
              onChangeView={handleChangeProviderView}
            >
              <DataLayoutChild view={DataLayoutViews.CHART}>
                <GridContainer alignItems="center" height="86px">
                  <GridItem md={6} xs={12}>
                    <Return onClick={handleResetProvider}>
                      <ReturnIcon />
                      <ReturnText>Volver a los proveedores del año {yearValue}</ReturnText>
                    </Return>
                  </GridItem>
                  <GridItem md={6} xs={12} />
                </GridContainer>
                <GridContainer padding="0 0 24px" spacing={1}>
                  <GridItem md={9} xs={12}>
                    <BubblesChart
                      categories={[
                        'Enero',
                        'Febrero',
                        'Marzo',
                        'Abril',
                        'Mayo',
                        'Junio',
                        'Julio',
                        'Agosto',
                        'Septiembre',
                        'Octubre',
                        'Noviembre',
                        'Diciembre'
                      ]}
                      data={provider.bubble}
                      id="regional-provider-bubbles-chart"
                      tooltipFormatter={handleTooltipFormatter}
                    />
                  </GridItem>
                  {renderProviderDetails()}
                </GridContainer>
              </DataLayoutChild>
              <DataLayoutChild view={DataLayoutCustomViews.CHART2}>
                <GridContainer alignItems="center" height="86px">
                  <GridItem md={6} xs={12}>
                    <Return onClick={handleResetProvider}>
                      <ReturnIcon />
                      <ReturnText>Volver a los proveedores del año {yearValue}</ReturnText>
                    </Return>
                  </GridItem>
                  <GridItem md={6} xs={12} />
                </GridContainer>
                <GridContainer padding="0 0 24px" spacing={1}>
                  <GridItem md={9} xs={12}>
                    <PieChart
                      data={enhancedProviderPieData}
                      hoverColor={theme.palette.charts.darkRed}
                      id="regional-provider-pie-chart"
                      tooltipFormatter={handlePieChartTooltipFormatter}
                    />
                  </GridItem>
                  {renderProviderDetails()}
                </GridContainer>
              </DataLayoutChild>
              <DataLayoutChild view={DataLayoutViews.TABLE}>
                <GridContainer padding="0 0 24px">
                  <GridItem xs={12}>
                    <DataTable
                      header={
                        <GridContainer alignItems="center" height="100%">
                          <GridItem md={6} xs={12}>
                            <Return onClick={handleResetProvider}>
                              <ReturnIcon />
                              <ReturnText>Volver a los proveedores del año {yearValue}</ReturnText>
                            </Return>
                          </GridItem>
                          <GridItem md={6} xs={12} />
                        </GridContainer>
                      }
                      headers={[
                        { label: 'Año', value: 0 },
                        { label: 'Mes', value: 1 },
                        { label: 'Proveedor', value: 2 },
                        { label: 'N° de órdenes de compra', value: 3 },
                        { label: 'Monto transado', value: 4 },
                        { label: 'Monto transado (USD)', value: 5 }
                      ]}
                      headersCellsProps={[
                        { enableFilter: true, enableSort: true, width: '10%' },
                        { enableFilter: true, enableSort: true, width: '10%' },
                        { enableFilter: true, enableSort: true, width: '35%' },
                        { enableSort: true, width: '15%' },
                        { enableFilter: true, enableSort: true, initialSort: true, width: '15%' },
                        { enableFilter: true, enableSort: true, width: '15%' }
                      ]}
                      initialRows={6}
                      loading={loading}
                      records={provider.bubbleCsv.slice(1)}
                      recordsDecorators={[
                        ([year]) => year,
                        ([, month]) => month,
                        ([, , name]) => name,
                        ([, , , orders]) => orders,
                        ([, , , , amount]) => `$${formatAmount(amount)}`,
                        ([, , , , , amountUSD]) => `$${formatAmount(amountUSD)}`
                      ]}
                    />
                  </GridItem>
                </GridContainer>
                <GridContainer padding="0 0 24px">
                  <GridItem align="right" xs={12}>
                    <DownloadDetails onClick={handleProviderDownloadFile}>
                      <DownloadDetailsCircle>
                        <DownloadDetailsIcon />
                      </DownloadDetailsCircle>
                      <DownloadDetailsText>Descargar estos datos</DownloadDetailsText>
                    </DownloadDetails>
                  </GridItem>
                </GridContainer>
                <GridContainer margin="6px 0 0">
                  <GridItem xs={12}>
                    <Info>
                      La información de montos transados dentro de esta sección corresponde al monto bruto (monto neto +
                      impuestos)
                    </Info>
                  </GridItem>
                </GridContainer>
              </DataLayoutChild>
            </DataLayout>
          </GridItem>
        </GridContainer>
      )}
    </Root>
  )
}

export default RegionalProvidersSection
